import ZoomInIcon from "@mui/icons-material/AddRounded";
import SaveIcon from "@mui/icons-material/FileDownloadOutlined";
import LeftIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import RightIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import DoubleLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import DoubleRightIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import ZoomOutIcon from "@mui/icons-material/RemoveRounded";
import RotateLeftIcon from "@mui/icons-material/RotateLeftRounded";
import RotateRightIcon from "@mui/icons-material/RotateRightRounded";
import type { Theme } from "@mui/material";
import { Divider, IconButton, Stack, Typography, useMediaQuery } from "@mui/material";
import saveAs from "file-saver";
import { useState } from "react";
import documentEventsApi from "../../../api/clients/documentEventsApi";
import SidebarIcon from "../../../icons/SidebarIcon";
import { logError } from "../../../logging";
import { printBlob } from "../../../utilities/printHelper";
import CloseIconButton from "../../CloseIconButton";
import HorizontalFill from "../../HorizontalFill";
import type { PdfViewerState } from "./pdfViewerState";

interface Props {
  fileName: string;
  viewerState: PdfViewerState;
  documentViewSessionId?: string;
  hideFileName?: boolean;
  hideDownloadButton?: boolean;
  hidePrintButton?: boolean;
  onNavigateToPage: (pageNumber: number) => void;
  onToggleThumbnails?: () => void;
  onZoomOut?: () => void;
  onZoomIn?: () => void;
  onRotateLeft?: () => void;
  onRotateRight?: () => void;
  onClose?: () => void;
}

const sendDownloadEvent = async (documentViewSessionId: string) => {
  try {
    await documentEventsApi.ingestDocumentDownloadEvent({
      viewEventsData: [
        {
          sessionId: documentViewSessionId,
          eventData: { download: true },
        },
      ],
    });
  } catch (error) {
    logError(error, "[PdfViewerControls] sendDownloadEvent");
  }
};

const PdfViewerControls = ({
  fileName,
  viewerState,
  documentViewSessionId,
  hideFileName,
  hideDownloadButton,
  hidePrintButton,
  onNavigateToPage,
  onToggleThumbnails,
  onZoomOut,
  onZoomIn,
  onRotateLeft,
  onRotateRight,
  onClose,
}: Props) => {
  const [isOperationInProgress, setIsOperationInProgress] = useState(false);

  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const { numPages, documentProxy, isDocumentLoaded } = viewerState;

  const handlePreviousPage = () => {
    onNavigateToPage(viewerState.currentPageNumber - 1);
  };

  const handleNextPage = () => {
    onNavigateToPage(viewerState.currentPageNumber + 1);
  };

  const handleFirstPage = () => {
    onNavigateToPage(1);
  };

  const handleLastPage = () => {
    onNavigateToPage(viewerState.numPages);
  };

  const handleDownloadClick = async () => {
    if (!documentProxy) {
      return;
    }

    setIsOperationInProgress(true);
    try {
      const documentData = await documentProxy.getData();
      const blob = new Blob([new Uint8Array(documentData)], { type: "application/pdf" });
      saveAs(blob, fileName);

      if (documentViewSessionId) {
        sendDownloadEvent(documentViewSessionId);
      }
    } catch (error) {
      logError(error, `[PdfViewerControls] Error downloading PDF ${fileName}`);
    } finally {
      setIsOperationInProgress(false);
    }
  };

  const handlePrintClick = async () => {
    if (!documentProxy) {
      return;
    }

    setIsOperationInProgress(true);
    try {
      const documentData = await documentProxy.getData();
      const blob = new Blob([new Uint8Array(documentData)], { type: "application/pdf" });
      await printBlob(blob);
    } catch (error) {
      logError(error, `[PdfViewerControls] Error printing PDF ${fileName}`);
    } finally {
      setIsOperationInProgress(false);
    }
  };

  const actionsDisabled = !isDocumentLoaded || isOperationInProgress;

  const spacing = isMobileScreen ? 0.5 : 1;

  return (
    <Stack direction="row" spacing={spacing} sx={{ alignItems: "center", justifyContent: "space-between" }}>
      {(onToggleThumbnails || !hideFileName) && (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", flex: 1, maxWidth: "40%" }}>
          {onToggleThumbnails && (
            <IconButton
              onClick={onToggleThumbnails}
              disabled={actionsDisabled}
              color={viewerState.showThumbnails ? "primary" : "secondary"}
            >
              <SidebarIcon />
            </IconButton>
          )}

          {!hideFileName && (
            <Typography variant="subtitle2" noWrap>
              {fileName}
            </Typography>
          )}
        </Stack>
      )}
      <Stack
        direction="row"
        spacing={spacing}
        sx={{ alignItems: "center", flexGrow: 0, flexShrink: 1, flexBasis: "auto" }}
      >
        <Stack direction="row" sx={{ alignItems: "center" }}>
          {!isMobileScreen && (
            <IconButton onClick={handleFirstPage} disabled={actionsDisabled || viewerState.currentPageNumber < 2}>
              <DoubleLeftIcon />
            </IconButton>
          )}

          {!isMobileScreen && (
            <IconButton onClick={handlePreviousPage} disabled={actionsDisabled || viewerState.currentPageNumber < 2}>
              <LeftIcon />
            </IconButton>
          )}
        </Stack>

        <Stack direction="row" spacing={spacing} sx={{ alignItems: "center" }}>
          <Typography noWrap>{isDocumentLoaded ? viewerState.currentPageNumber : ""}</Typography>
          <Typography noWrap>{isDocumentLoaded ? `of ${viewerState.numPages}` : ""}</Typography>
        </Stack>

        <Stack direction="row" sx={{ alignItems: "center" }}>
          {!isMobileScreen && (
            <IconButton
              onClick={handleNextPage}
              disabled={actionsDisabled || viewerState.currentPageNumber > numPages - 1}
            >
              <RightIcon />
            </IconButton>
          )}

          {!isMobileScreen && (
            <IconButton
              onClick={handleLastPage}
              disabled={actionsDisabled || viewerState.currentPageNumber > numPages - 1}
            >
              <DoubleRightIcon />
            </IconButton>
          )}
        </Stack>

        <Divider flexItem orientation="vertical" />

        {onZoomOut && (
          <IconButton onClick={onZoomOut} disabled={actionsDisabled}>
            <ZoomOutIcon />
          </IconButton>
        )}

        {(onZoomIn || onZoomOut) && <Typography noWrap>{viewerState.scalePercent}%</Typography>}

        {onZoomIn && (
          <IconButton onClick={onZoomIn} disabled={actionsDisabled}>
            <ZoomInIcon />
          </IconButton>
        )}

        {onRotateLeft && (
          <IconButton onClick={onRotateLeft} disabled={actionsDisabled}>
            <RotateLeftIcon />
          </IconButton>
        )}

        {onRotateRight && (
          <IconButton onClick={onRotateRight} disabled={actionsDisabled}>
            <RotateRightIcon />
          </IconButton>
        )}
      </Stack>
      <Stack direction="row" spacing={spacing} sx={{ alignItems: "center", flex: 1 }}>
        <HorizontalFill />

        {!hideDownloadButton && (
          <IconButton onClick={handleDownloadClick} disabled={actionsDisabled}>
            <SaveIcon />
          </IconButton>
        )}

        {!hidePrintButton && (
          <IconButton onClick={handlePrintClick} disabled={actionsDisabled}>
            <PrintIcon />
          </IconButton>
        )}

        {onClose && <CloseIconButton onClick={onClose} />}
      </Stack>
    </Stack>
  );
};

export default PdfViewerControls;
